import {
  Component, OnDestroy, OnInit,
  inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl, FormGroup, ReactiveFormsModule, Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Timestamp } from '@angular/fire/firestore';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subject, takeUntil } from 'rxjs';
import { ResolvedAddress } from '../../../models/resolved-address.model';
import {
  CRM_ACTIVITY_TYPE_LABELS,
  CRM_LEAD_STATUSES,
  CRM_SOURCE_LABELS,
  CRM_STATUS_LABELS,
  CRM_VENUE_TYPE_LABELS,
  CrmActivity,
  CrmActivityType,
  CrmLead,
  CrmLeadSource,
  CrmLeadStatus,
  CrmVenue,
  CrmVenueType,
} from '../../../models/crm-lead.model';
import { AdminCrmDeleteLeadModalComponent } from '../admin-crm-delete-lead-modal/admin-crm-delete-lead-modal.component';
import { AdminCrmLeadOnboardingModalComponent } from '../admin-crm-lead-onboarding-modal/admin-crm-lead-onboarding-modal.component';
import { CrmLeadInput, CrmLeadService, CrmVenueInput } from '../../../services/crm-lead.service';

@Component({
  selector: 'app-admin-crm-lead-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    AdminCrmLeadOnboardingModalComponent,
    AdminCrmDeleteLeadModalComponent,
  ],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-crm-lead-detail.component.html',
})
export class AdminCrmLeadDetailComponent implements OnInit, OnDestroy {
  private readonly crm = inject(CrmLeadService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly venuesDestroy$ = new Subject<void>();

  readonly isNew = signal(false);
  readonly leadId = signal<string | null>(null);
  readonly lead = signal<CrmLead | null>(null);
  readonly venues = signal<CrmVenue[]>([]);
  readonly activities = signal<CrmActivity[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly copyFeedback = signal<string | null>(null);
  readonly showAddVenue = signal(false);
  readonly showDeleteConfirm = signal(false);
  readonly deleting = signal(false);
  readonly venueImportFeedback = signal<string | null>(null);

  readonly CRM_STATUS_LABELS = CRM_STATUS_LABELS;
  readonly CRM_SOURCE_LABELS = CRM_SOURCE_LABELS;
  readonly CRM_VENUE_TYPE_LABELS = CRM_VENUE_TYPE_LABELS;
  readonly CRM_ACTIVITY_TYPE_LABELS = CRM_ACTIVITY_TYPE_LABELS;
  readonly CRM_LEAD_STATUSES = CRM_LEAD_STATUSES;

  readonly leadForm = new FormGroup({
    status:            new FormControl<CrmLeadStatus>('identified', { nonNullable: true }),
    priority:          new FormControl<'A' | 'B' | 'C'>('B', { nonNullable: true }),
    venueName:         new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(2)] }),
    venueType:         new FormControl<CrmVenueType>('sala_fiestas', { nonNullable: true }),
    city:              new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    province:          new FormControl('', { nonNullable: true }),
    mapsUrl:           new FormControl('', { nonNullable: true }),
    contactName:       new FormControl('', { nonNullable: true }),
    phone:             new FormControl('', { nonNullable: true }),
    email:             new FormControl('', { nonNullable: true }),
    source:            new FormControl<CrmLeadSource>('maps', { nonNullable: true }),
    sourceDetail:      new FormControl('', { nonNullable: true }),
    notes:             new FormControl('', { nonNullable: true }),
    nextAction:        new FormControl('Primer contacto', { nonNullable: true, validators: [Validators.required] }),
    nextActionAt:      new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    nextActionNote:    new FormControl('', { nonNullable: true }),
    lostReason:        new FormControl('', { nonNullable: true }),
    ownerUid:          new FormControl('', { nonNullable: true }),
    spaceId:           new FormControl('', { nonNullable: true }),
    referralBookingId: new FormControl('', { nonNullable: true }),
  });

  readonly activityForm = new FormGroup({
    type: new FormControl<CrmActivityType>('whatsapp', { nonNullable: true }),
    body: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
  });

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        if (!id) return;
        const isNew = id === 'nuevo';
        this.isNew.set(isNew);
        this.leadId.set(isNew ? null : id);
        this.loading.set(!isNew);
        if (isNew) {
          this.resetNewForm();
          this.loading.set(false);
          return;
        }
        this.loadLead(id);
      });
  }

  ngOnDestroy(): void {
    this.venuesDestroy$.next();
    this.venuesDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    void this.router.navigate(['/admin/crm/leads']);
  }

  leadDeleteLabel(): string {
    const l = this.lead();
    if (!l) return 'este lead';
    return l.contactName || l.venueName || 'este lead';
  }

  openDeleteConfirm(): void {
    this.showDeleteConfirm.set(true);
  }

  closeDeleteConfirm(): void {
    if (this.deleting()) return;
    this.showDeleteConfirm.set(false);
  }

  async confirmDeleteLead(): Promise<void> {
    const id = this.leadId();
    if (!id) return;
    this.deleting.set(true);
    this.saveError.set(null);
    try {
      await this.crm.deleteLead(id);
      this.showDeleteConfirm.set(false);
      void this.router.navigate(['/admin/crm/leads']);
    } catch (err: unknown) {
      this.saveError.set(err instanceof Error ? err.message : 'No se pudo eliminar el lead.');
      this.deleting.set(false);
    }
  }

  openAddVenue(): void {
    this.venueImportFeedback.set(null);
    this.showAddVenue.set(true);
  }

  closeAddVenue(): void {
    this.showAddVenue.set(false);
  }

  async onAddVenueFromMaps(payload: { resolved: ResolvedAddress; mapsUrl: string }): Promise<void> {
    const id = this.leadId();
    if (!id) return;
    this.saving.set(true);
    this.venueImportFeedback.set(null);
    try {
      const result = await this.crm.addVenueFromMapsToLead(id, payload.resolved, payload.mapsUrl);
      this.showAddVenue.set(false);
      if (result.leadId !== id) {
        this.venueImportFeedback.set('El teléfono coincide con otro contacto; redirigiendo…');
        void this.router.navigate(['/admin/crm/leads', result.leadId]);
      } else {
        this.venueImportFeedback.set('Local añadido al contacto.');
      }
    } catch (err: unknown) {
      this.venueImportFeedback.set(err instanceof Error ? err.message : 'Error al importar local.');
    } finally {
      this.saving.set(false);
    }
  }

  onAddVenueManual(): void {
    this.showAddVenue.set(false);
    this.venueImportFeedback.set('Usa Google Maps para añadir otro local con datos completos, o edita los campos del local principal abajo.');
  }

  formatDate(value: unknown): string {
    if (!value) return '—';
    const date = value instanceof Timestamp ? value.toDate() : new Date(value as string);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  formatTs(value: unknown): string {
    if (!value) return '—';
    const date = value instanceof Timestamp ? value.toDate() : new Date(value as string);
    return date.toLocaleString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  registrationUrl(): string {
    const id = this.leadId();
    if (!id) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/login?ref=lead-${id}`;
  }

  async copyRegistrationLink(): Promise<void> {
    const id = this.leadId();
    if (!id) return;
    try {
      await navigator.clipboard.writeText(this.registrationUrl());
      this.copyFeedback.set('Enlace copiado');
      setTimeout(() => this.copyFeedback.set(null), 2000);
    } catch {
      this.copyFeedback.set('No se pudo copiar');
    }
  }

  async saveNotesOnBlur(): Promise<void> {
    const id = this.leadId();
    if (!id || this.isNew()) return;
    const notes = this.leadForm.controls.notes.value.trim();
    const current = this.lead()?.notes ?? '';
    if (notes === current) return;
    await this.crm.updateLead(id, { notes });
  }

  async saveLead(): Promise<void> {
    if (this.leadForm.invalid) {
      this.leadForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.saveError.set(null);
    try {
      if (this.isNew()) {
        const { lead, venue } = this.formToLeadAndVenue();
        const id = await this.crm.createLeadWithVenue(lead, venue);
        void this.router.navigate(['/admin/crm/leads', id]);
        return;
      }
      const payload = this.formToLeadInput();
      const prev = this.lead();
      const id = this.leadId()!;
      if (prev && prev.status !== payload.status) {
        await this.crm.changeStatus(id, prev.status, payload.status);
        const { status: _s, ...rest } = payload;
        await this.crm.updateLead(id, rest);
      } else {
        await this.crm.updateLead(id, payload);
      }
    } catch (err: unknown) {
      this.saveError.set(err instanceof Error ? err.message : 'Error al guardar.');
    } finally {
      this.saving.set(false);
    }
  }

  async addActivity(): Promise<void> {
    const id = this.leadId();
    if (!id || this.activityForm.invalid) {
      this.activityForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    try {
      const { type, body } = this.activityForm.getRawValue();
      await this.crm.addActivity(id, type, body);
      this.activityForm.reset({ type: 'whatsapp', body: '' });
    } catch (err: unknown) {
      this.saveError.set(err instanceof Error ? err.message : 'Error al registrar actividad.');
    } finally {
      this.saving.set(false);
    }
  }

  private loadLead(id: string): void {
    this.crm.watchLeads()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: leads => {
          const found = leads.find(l => l.id === id) ?? null;
          this.lead.set(found);
          this.loading.set(false);
          if (found) this.patchLeadForm(found);
          if (!found && !this.isNew()) {
            this.saveError.set('Lead no encontrado.');
          }
        },
        error: err => {
          this.loading.set(false);
          this.saveError.set(err?.message ?? 'Error al cargar el lead.');
        },
      });

    this.crm.watchActivities(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(acts => this.activities.set(acts));

    this.venuesDestroy$.next();
    this.crm.watchVenues(id)
      .pipe(takeUntil(this.venuesDestroy$), takeUntil(this.destroy$))
      .subscribe(v => this.venues.set(v));
  }

  private resetNewForm(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    this.leadForm.reset({
      status: 'identified',
      priority: 'B',
      venueName: '',
      venueType: 'sala_fiestas',
      city: '',
      province: '',
      mapsUrl: '',
      contactName: '',
      phone: '',
      email: '',
      source: 'other',
      sourceDetail: '',
      notes: '',
      nextAction: 'Primer contacto',
      nextActionAt: this.toDatetimeLocal(Timestamp.fromDate(tomorrow)),
      nextActionNote: '',
      lostReason: '',
      ownerUid: '',
      spaceId: '',
      referralBookingId: '',
    });
  }

  private patchLeadForm(lead: CrmLead): void {
    const primary = this.venues()[0];
    this.leadForm.patchValue({
      status: lead.status,
      priority: lead.priority,
      venueName: primary?.venueName ?? lead.venueName,
      venueType: primary?.venueType ?? lead.venueType,
      city: primary?.city ?? lead.city,
      province: primary?.province ?? lead.province ?? '',
      mapsUrl: primary?.mapsUrl ?? lead.mapsUrl ?? '',
      contactName: lead.contactName ?? '',
      phone: lead.phone ?? '',
      email: lead.email ?? '',
      source: lead.source,
      sourceDetail: lead.sourceDetail ?? '',
      notes: lead.notes ?? '',
      nextAction: lead.nextAction,
      nextActionAt: this.toDatetimeLocal(lead.nextActionAt),
      nextActionNote: lead.nextAction,
      lostReason: lead.lostReason ?? '',
      ownerUid: lead.ownerUid ?? '',
      spaceId: lead.spaceId ?? '',
      referralBookingId: lead.referralBookingId ?? '',
    });
  }

  private formToLeadInput(): CrmLeadInput {
    const v = this.leadForm.getRawValue();
    const nextActionAt = Timestamp.fromDate(new Date(v.nextActionAt));
    const nextAction = (v.nextActionNote || v.nextAction).trim();
    const input: CrmLeadInput = {
      status: v.status,
      priority: v.priority,
      venueName: v.venueName.trim(),
      venueType: v.venueType,
      city: v.city.trim(),
      source: v.source,
      nextAction,
      nextActionAt,
    };
    if (v.province.trim()) input.province = v.province.trim();
    if (v.mapsUrl.trim()) input.mapsUrl = v.mapsUrl.trim();
    if (v.contactName.trim()) input.contactName = v.contactName.trim();
    if (v.phone.trim()) input.phone = v.phone.trim();
    if (v.email.trim()) input.email = v.email.trim();
    if (v.sourceDetail.trim()) input.sourceDetail = v.sourceDetail.trim();
    if (v.notes.trim()) input.notes = v.notes.trim();
    if (v.status === 'lost' && v.lostReason.trim()) input.lostReason = v.lostReason.trim();
    if (v.ownerUid.trim()) input.ownerUid = v.ownerUid.trim();
    if (v.spaceId.trim()) input.spaceId = v.spaceId.trim();
    if (v.referralBookingId.trim()) input.referralBookingId = v.referralBookingId.trim();
    return input;
  }

  private formToLeadAndVenue(): { lead: CrmLeadInput; venue: CrmVenueInput } {
    const lead = this.formToLeadInput();
    const v = this.leadForm.getRawValue();
    const venue: CrmVenueInput = {
      venueName: v.venueName.trim(),
      venueType: v.venueType,
      city: v.city.trim(),
      source: 'manual',
      ...(v.province.trim() ? { province: v.province.trim() } : {}),
      ...(v.mapsUrl.trim() ? { mapsUrl: v.mapsUrl.trim() } : {}),
    };
    return { lead, venue };
  }

  private toDatetimeLocal(ts: Timestamp): string {
    const d = ts.toDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
